import React from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  HelpOutline as AskAiIcon,
  Preview as PreviewIcon,
  Undo as UndoIcon,
  UnfoldLess as CollapseAllIcon,
  UnfoldMore as ExpandAllIcon,
} from '@mui/icons-material';
import type { CanvasNode, CanvasEdge } from './types';

export interface CanvasContextMenuProps {
  /** Screen position of the context menu. */
  position: { x: number; y: number } | null;
  /** The node that was right-clicked (if any). */
  node?: CanvasNode | null;
  /** The edge that was right-clicked (if any). */
  edge?: CanvasEdge | null;
  /** Close the menu. */
  onClose: () => void;
  /** Action handlers — mapping-level. */
  onEditTransformation?: () => void;
  onAiRegenerate?: () => void;
  onAskAi?: () => void;
  onApprove?: () => void;
  onUndo?: () => void;
  onDelete?: () => void;
  /** Action handlers — table group-level. */
  onToggleExpand?: () => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onPreviewTable?: () => void;
}

const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
  position,
  node,
  edge,
  onClose,
  onEditTransformation,
  onAiRegenerate,
  onAskAi,
  onApprove,
  onUndo,
  onDelete,
  onToggleExpand,
  onExpandAll,
  onCollapseAll,
  onPreviewTable,
}) => {
  const open = Boolean(position);
  const hasMapping = Boolean(
    node?.data?.mapping ||
    edge?.data?.mappingId
  );
  const isTableGroup = node?.data?.kind === 'tableGroup';
  const isExpanded = node?.data?.expanded ?? node?.data?.groupExpanded ?? false;

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ? { top: position.y, left: position.x } : undefined}
      PaperProps={{
        sx: {
          minWidth: 200,
          boxShadow: '0 8px 24px rgba(15,23,42,0.14)',
          borderRadius: 2,
        },
      }}
    >
      {/* Table group actions */}
      {isTableGroup && onToggleExpand && (
        <MenuItem onClick={() => { onToggleExpand(); onClose(); }}>
          <ListItemIcon>{isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}</ListItemIcon>
          <ListItemText primary={isExpanded ? 'Collapse Table' : 'Expand Table'} primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {isTableGroup && onPreviewTable && (
        <MenuItem onClick={() => { onPreviewTable(); onClose(); }}>
          <ListItemIcon><PreviewIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Preview Table Data" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {isTableGroup && (onExpandAll || onCollapseAll) && (
        <Divider sx={{ my: 0.5 }} />
      )}

      {isTableGroup && onExpandAll && (
        <MenuItem onClick={() => { onExpandAll(); onClose(); }}>
          <ListItemIcon><ExpandAllIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Expand All Tables" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {isTableGroup && onCollapseAll && (
        <MenuItem onClick={() => { onCollapseAll(); onClose(); }}>
          <ListItemIcon><CollapseAllIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Collapse All Tables" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {/* Mapping-level actions */}
      {hasMapping && onEditTransformation && (
        <MenuItem onClick={() => { onEditTransformation(); onClose(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit Transformation" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {hasMapping && onAiRegenerate && (
        <MenuItem onClick={() => { onAiRegenerate(); onClose(); }}>
          <ListItemIcon><AiIcon fontSize="small" sx={{ color: '#2563eb' }} /></ListItemIcon>
          <ListItemText primary="AI Regenerate" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {hasMapping && onAskAi && (
        <MenuItem onClick={() => { onAskAi(); onClose(); }}>
          <ListItemIcon><AskAiIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Ask AI About This" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {hasMapping && onApprove && (
        <MenuItem onClick={() => { onApprove(); onClose(); }}>
          <ListItemIcon><ApproveIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
          <ListItemText primary="Approve Mapping" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {(hasMapping && (onEditTransformation || onAiRegenerate || onAskAi || onApprove)) && (
        <Divider sx={{ my: 0.5 }} />
      )}

      {onUndo && (
        <MenuItem onClick={() => { onUndo(); onClose(); }}>
          <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Undo Last Change" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}

      {hasMapping && onDelete && (
        <MenuItem onClick={() => { onDelete(); onClose(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Delete Mapping" primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      )}
    </Menu>
  );
};

export default CanvasContextMenu;
